import { useEffect, useRef, useMemo, useState } from 'react';

export function useMRTListController({ mrt, contextKey, resolveBoot, fetchData, contextReady = true, bootMode = 'date' }) {
    const [mrtBooted, setMrtBooted] = useState(false);
    const fetchDataRef = useRef(fetchData);

    useEffect(() => {
        fetchDataRef.current = fetchData;
    }, [fetchData]);

    const hasMrt = !!mrt;

    const safeColumnFilters = mrt?.columnFilters ?? [];
    const safeColumnFilterFns = mrt?.columnFilterFns ?? {};
    const safeSorting = mrt?.sorting ?? [];

    const boot = useMemo(() => {
        if (!hasMrt) return null;
        return resolveBoot?.() || null;
    }, [hasMrt, resolveBoot, contextKey]);

    // =========================================================
    // RESET ao trocar contexto
    // =========================================================
    useEffect(() => {
        if (!hasMrt) return;

        setMrtBooted(false);

        mrt.setColumnFilters?.([]);
        mrt.setSorting?.([]);
        mrt.setGlobalFilter?.('');

        if (typeof mrt.setColumnFilterFns === 'function') {
            mrt.setColumnFilterFns({});
        } else if (typeof mrt.setColumnFiltersMode === 'function') {
            mrt.setColumnFiltersMode({});
        }

        mrt.setPagination?.((prev) => ({
            ...prev,
            pageIndex: 0,
        }));
    }, [hasMrt, contextKey]);

    // =========================================================
    // APLICA BOOT
    // =========================================================
    useEffect(() => {
        if (!hasMrt) return;
        if (mrtBooted) return;

        // sem boot = libera o fetch
        if (!boot) {
            setMrtBooted(true);
            return;
        }

        if (bootMode === 'simple') {
            if (boot.columnFilterFns) {
                if (typeof mrt.setColumnFilterFns === 'function') {
                    mrt.setColumnFilterFns(boot.columnFilterFns);
                } else if (typeof mrt.setColumnFiltersMode === 'function') {
                    mrt.setColumnFiltersMode(boot.columnFilterFns);
                }
            }

            if (boot.columnFilters) {
                mrt.setColumnFilters?.(boot.columnFilters);
            }

            if (boot.sorting) {
                mrt.setSorting?.(boot.sorting);
            }

            setMrtBooted(true);
            return;
        }

        const dataFilter = Array.isArray(safeColumnFilters) ? safeColumnFilters.find((f) => f.id === 'data') : null;

        const hasDateRange = dataFilter && Array.isArray(dataFilter.value) && dataFilter.value.length === 2;

        const hasDateFilterFn = safeColumnFilterFns?.data === 'between';
        const hasSorting = Array.isArray(safeSorting) && safeSorting.length > 0;

        let changed = false;

        if (!hasDateFilterFn && boot.columnFilterFns) {
            if (typeof mrt.setColumnFilterFns === 'function') {
                mrt.setColumnFilterFns(boot.columnFilterFns);
            } else if (typeof mrt.setColumnFiltersMode === 'function') {
                mrt.setColumnFiltersMode(boot.columnFilterFns);
            }
            changed = true;
        }

        if (!hasDateRange && boot.columnFilters) {
            mrt.setColumnFilters?.(boot.columnFilters);
            changed = true;
        }

        if (!hasSorting && boot.sorting) {
            mrt.setSorting?.(boot.sorting);
            changed = true;
        }

        // espera próximo ciclo após aplicar boot
        if (changed) return;

        setMrtBooted(true);
    }, [hasMrt, mrtBooted, boot, safeColumnFilters, safeColumnFilterFns, safeSorting, mrt]);

    // =========================================================
    // FETCH CONTROLADO
    // =========================================================
    useEffect(() => {
        if (!hasMrt) return;
        if (!contextReady) return;
        if (!mrtBooted) return;
        if (typeof fetchDataRef.current !== 'function') return;

        fetchDataRef.current();
    }, [
        hasMrt,
        contextReady,
        mrtBooted,
        mrt?.columnFilters,
        mrt?.columnFilterFns,
        mrt?.globalFilter,
        mrt?.pagination?.pageIndex,
        mrt?.pagination?.pageSize,
        mrt?.sorting,
    ]);

    return {
        mrtBooted,
    };
}
